import { Delivery } from '../components/up-board/up-board.component';

/**
 * Utility class for date/time operations
 * 
 * Provides duration calculations and time comparisons using native JavaScript Date APIs.
 * All times are expected to be JavaScript Date objects or Unix timestamps.
 */
export class TimeUtils {

  constructor() {
  }

  /**
   * Calculate duration in milliseconds between two dates
   * @param startTime Start date/timestamp
   * @param endTime End date/timestamp
   * @returns Duration in milliseconds
   */
  private getDurationMs(startTime: Date | undefined, endTime: Date): number {
    if (!startTime) return 0;
    const start = startTime instanceof Date ? startTime.getTime() : startTime;
    const end = endTime instanceof Date ? endTime.getTime() : endTime;
    return Math.abs(end - start);
  }

  /**
   * Convert milliseconds to minutes
   */
  private msToMinutes(ms: number): number {
    return ms / 60000;
  }

  /**
   * Convert milliseconds to a duration object with hours, minutes, seconds
   */
  private msToDuration(ms: number): { hours: number; minutes: number; seconds: number } {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  }

  /**
   * Check if delivery is on schedule
   * - If customer in store: must be there less than 5 minutes
   * - If not in store: must arrive within 75 minutes
   */
  isOnSchedule(delivery: Delivery, now: Date) {
    return (delivery.customer_in_store && !this.inStoreLongerThan(delivery.arrival_time, now, 5)) ||
      (!delivery.customer_in_store && !this.arrivingInLessThan(delivery.arrival_time, now, 75));
  }

  /**
   * Check if delivery is in warning status
   * - If customer in store: 5-10 minutes
   * - If not in store: 45-75 minutes away
   */
  isWarning(delivery: Delivery, now: Date) {
    return (delivery.customer_in_store && this.inStoreLongerThan(delivery.arrival_time, now, 5) && !this.inStoreLongerThan(delivery.arrival_time, now, 10)) ||
      (!delivery.customer_in_store && !this.arrivingInLessThan(delivery.arrival_time, now, 45) && this.arrivingInLessThan(delivery.arrival_time, now, 75));
  }

  /**
   * Check if delivery is in alert status
   * - If customer in store: longer than 10 minutes
   * - If not in store: arriving in less than 45 minutes
   */
  isAlert(delivery: Delivery, now: Date) {
    return (delivery.customer_in_store && this.inStoreLongerThan(delivery.arrival_time, now, 10)) ||
      (!delivery.customer_in_store && this.arrivingInLessThan(delivery.arrival_time, now, 45));
  }

  /**
   * Format a duration in milliseconds to human-readable string
   * Examples: "2 hours 30 min", "45 min", "30 sec"
   */
  formatDuration(durationMs: number): string {
    const duration = this.msToDuration(durationMs);
    let str = '';

    if (duration.hours > 0) {
      str += `${duration.hours} hour`;
      if (duration.hours > 1) str += 's';
    }

    if (duration.minutes > 0) {
      if (str) str += ' ';
      str += `${duration.minutes} min`;
    }

    if (duration.hours === 0 && duration.seconds > 0) {
      if (str) str += ' ';
      str += `${duration.seconds} sec`;
    }

    return str || '0 sec';
  }

  /**
   * Get human-readable string of time until arrival or time since arrival
   * 
   * Examples:
   * - "in 2 hours" (if arriving in future)
   * - "2 hours ago" (if already arrived)
   * - "30 min" (if within 2 hours)
   */
  arrivingInStr(time: Date | undefined, now: Date): string {
    if (!time) return '';

    const durationMs = this.getDurationMs(time, now);
    const timeObj = time instanceof Date ? time : new Date(time);
    const nowObj = now instanceof Date ? now : new Date(now);

    // If arriving more than 2 hours in future, use humanized format
    if (timeObj > nowObj && this.getDurationMs(timeObj, nowObj) > 2 * 60 * 60 * 1000) {
      const durationMins = this.msToMinutes(durationMs);
      if (durationMins < 60) {
        return `in ${Math.round(durationMins)} min`;
      } else {
        const hours = Math.floor(durationMins / 60);
        const mins = Math.round(durationMins % 60);
        return mins > 0 ? `in ${hours}h ${mins}m` : `in ${hours}h`;
      }
    }

    // If already arrived (past), show as "ago"
    if (timeObj < nowObj) {
      const durationMins = this.msToMinutes(durationMs);
      if (durationMins < 60) {
        return `${Math.round(durationMins)} min ago`;
      } else {
        const hours = Math.floor(durationMins / 60);
        const mins = Math.round(durationMins % 60);
        return mins > 0 ? `${hours}h ${mins}m ago` : `${hours}h ago`;
      }
    }

    // Default: format as duration
    return this.formatDuration(durationMs);
  }

  /**
   * Get human-readable string of how long customer has been in store
   */
  inStoreForStr(time: Date | undefined, now: Date): string {
    if (!time) return '';
    const durationMs = this.getDurationMs(time, now);
    return this.formatDuration(durationMs);
  }

  /**
   * Get difference between two times in minutes
   */
  diffAsMin(time: Date | undefined, now: Date): number {
    if (!time) return 0;
    return this.msToMinutes(this.getDurationMs(time, now));
  }

  /**
   * Check if arrival time is less than threshold minutes away
   */
  arrivingInLessThan(time: Date | undefined, now: Date, threshold: number): boolean {
    if (!time) return false;
    const durationMins = this.msToMinutes(this.getDurationMs(time, now));
    const timeObj = time instanceof Date ? time : new Date(time);
    const nowObj = now instanceof Date ? now : new Date(now);
    // Only count future arrivals
    return timeObj > nowObj && durationMins < threshold;
  }

  /**
   * Get minutes until arrival
   * Returns negative if already arrived
   */
  arrivingInMin(time: Date | undefined, now: Date): number {
    if (!time) return 0;
    const timeObj = time instanceof Date ? time : new Date(time);
    const nowObj = now instanceof Date ? now : new Date(now);
    const durationMins = this.msToMinutes(timeObj.getTime() - nowObj.getTime());
    return durationMins;
  }

  /**
   * Check if customer has been in store longer than threshold minutes
   */
  inStoreLongerThan(time: Date | undefined, now: Date, threshold: number): boolean {
    if (!time) return false;
    const durationMins = this.msToMinutes(this.getDurationMs(time, now));
    return durationMins > threshold;
  }
}
